import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';

import { getLocations } from '../api/services/Locations';
import { LOCATION_TYPES } from '../constants/locationTypes';

export type GroupedOption = {
  group: string;
  label: string;
  placeId: string;
  dropoffProcedureInstructions: string;
  pickupProcedureInstructions: string;
  passengerCount: string;
  id?: string;
  name?: string;
  address?: string;
  calendar?: string;
  calendarName?: string;
  latLng?: string;
  preciseLatLng?: string;
  locationType?: string;
  riderSlug?: string;
  primary?: boolean;
  streetAddressTwo?: string;
};

interface UseRiderLocationOptionsHook {
  queryRiderLocations: (query: string) => void;
  associatedLocationOptions: GroupedOption[] | undefined;
  setRiderSlugs: Dispatch<SetStateAction<string[]>>;
}

export const useRiderLocationOptions = (): UseRiderLocationOptionsHook => {
  const [riderSlugs, setRiderSlugs] = useState<string[]>([]);
  const [querySearch, setQuerySearch] = useState<string>();
  const [associatedLocations, setAssociatedLocations] = useState<GroupedOption[] | undefined>(undefined);

  const fetchAssociatedLocations = useCallback(async () => {
    const response = await getLocations({
      page: 1,
      perPage: 100,
      assignedTo: riderSlugs,
    });

    if (response?.type === 'success' && response.data?.locations) {
      setAssociatedLocations(formatLocations(response.data.locations));
    }
  }, [riderSlugs]);

  // this is needed due to our SelectLocation Autocomplete not autocompleting and using the Api filtered results
  const queryRiderLocations = debounce((query) => setQuerySearch(query), 400);

  useEffect(() => {
    if (riderSlugs.length) {
      fetchAssociatedLocations();
    }
  }, [riderSlugs]);

  return {
    queryRiderLocations,
    associatedLocationOptions: querySearch
      ? associatedLocations?.filter((location) => isOptionMatch(querySearch, location.name || location.address))
      : associatedLocations,
    setRiderSlugs,
  };
};

function formatLocations(locations: AccountLocation[]) {
  return locations.map((loc) => {
    const locationType = LOCATION_TYPES.find((type) => type.value === loc?.locationType)?.label;

    return {
      group: 'Rider Assigned Locations:',
      label: loc.name
        ? `${loc.name} - ${locationType}`
        : `${loc.address.split(',').slice(0, 2).join(',')} - ${locationType}`,
      placeId: loc.placeId,
      pickupProcedureInstructions: loc.pickupProcedure?.instructions,
      dropoffProcedureInstructions: loc.dropoffProcedure?.instructions,
      passengerCount: `${loc.passengerCount || 0}`,
      name: loc.name,
      address: loc.address,
      streetAddressTwo: loc.streetAddressTwo || '',
      calendar: `${loc.calendar?.slug || ''}`,
      calendarName: `${loc.calendar?.name || ''}`,
      id: loc.id.toString(),
      latLng: `${loc.lat},${loc.lng}`,
      preciseLatLng: loc.preciseLocation?.lat ? `${loc.preciseLocation.lat},${loc.preciseLocation.lng}` : undefined,
      locationType: loc.locationType || 'other',
      riderSlug: loc.riderSlug,
      primary: loc.primary || false,
    };
  });
}

const isOptionMatch = (inputValue, optionLabel) => {
  return optionLabel.toLocaleLowerCase().indexOf(inputValue.toLocaleLowerCase()) !== -1;
};
